import React, { useState, useContext } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Box
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { axios } from 'api'
import Cookies from 'js-cookie'
import { cookieOptions } from 'utils/cookieOptions'
import { useNavigate } from 'react-router-dom'
import { validatePasswordComplexity } from 'utils/formatUtils'
import { SnackbarContext } from 'contexts/SnackbarContext'

type FormData = {
  newPassword: string
  confirmPassword: string
}

interface Props {
  open: boolean
  session: string | null
  username: string | null
  onClose: () => void
  onSuccess: () => void
}

const ChangePasswordDialog: React.FC<Props> = ({
  open,
  session,
  username,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showMessage } = useContext(SnackbarContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: { newPassword: '', confirmPassword: '' }
  })

  const newPassword = watch('newPassword')

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      if (!session || !username) {
        setErrorMessage('Session expired. Please login again.')
        setTimeout(() => {
          onClose()
          navigate('/login')
        }, 2000)
        return
      }

      const response = await axios.post('/accounts/complete-new-password', {
        email: username,
        newPassword: data.newPassword,
        session
      })

      if (response.status === 200) {
        const { accessToken, refreshToken } = response.data

        Cookies.set('accessToken', accessToken, cookieOptions)
        Cookies.set('refreshToken', refreshToken, cookieOptions)
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

        showMessage(t('snackbar.account.passwordUpdated'))
        onSuccess()
        onClose()
      } else {
        setErrorMessage(
          response?.data?.error ||
            'Failed to update password. Please try again.'
        )
      }
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.error ||
          'An unexpected error occurred. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <DialogTitle>Change Your Password</DialogTitle>
      <DialogContent>
        <Alert severity='warning' sx={{ mb: 3 }}>
          Your password is temporary. Please set a new password to continue.
        </Alert>

        {errorMessage && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name='newPassword'
            control={control}
            rules={{
              required: 'New password is required',
              validate: value => validatePasswordComplexity(value) || true
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label='New Password'
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                margin='normal'
                disabled={isSubmitting}
                inputProps={{ maxLength: 64 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowNewPassword(prev => !prev)}
                        edge='end'
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
              />
            )}
          />

          <Controller
            name='confirmPassword'
            control={control}
            rules={{
              required: 'Please confirm your password',
              validate: value =>
                value === newPassword || 'Passwords do not match'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Confirm New Password'
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                margin='normal'
                disabled={isSubmitting}
                inputProps={{ maxLength: 64 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        edge='end'
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                InputLabelProps={{ shrink: true }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ChangePasswordDialog
